import dayjs from "dayjs"
import pluralize from "pluralize"

import { RecurrenceFrequency } from "@constants/choices"

interface FormatRecurrenceParams {
    interval: number
    frequency: number
    startDate: string
    weeklyRecurrence: number[]
    monthlyRecurrence: number
}

/**
 * Converts a dayjs .day() value (0=Sunday) to our Monday-based weekday index (0=Monday).
 */
export function dayjsDayToWeekdayIndex(dayjsDay: number): number {
    return (dayjsDay + 6) % 7
}

interface MonthlyRecurrenceOption {
    value: number
    label: string
}

export function getMonthlyRecurrenceOptions(startDate: Date | string | undefined): MonthlyRecurrenceOption[] {
    if (!startDate) {
        return [
            { value: 0, label: "Day of month" },
            { value: 1, label: "Weekday of month" },
        ]
    }

    const date = dayjs(startDate)
    const dayOfMonth = date.format("D")
    const dayOfWeek = date.format("dddd")

    const firstOfMonth = date.startOf("month")
    const occurrence = Math.floor((date.date() - firstOfMonth.date()) / 7) + 1
    const occurrenceFormatted = dayjs().date(occurrence).format("Do")

    const sameDayLabel = occurrence > 4 ? `The last ${dayOfWeek}` : `The ${occurrenceFormatted} ${dayOfWeek}`

    return [
        { value: 0, label: `Day ${dayOfMonth}` },
        { value: 1, label: sameDayLabel },
    ]
}

export default function formatRecurrence(params: FormatRecurrenceParams): string {
    const { interval, frequency, startDate, weeklyRecurrence, monthlyRecurrence } = params

    const date = dayjs(startDate)

    if (frequency === RecurrenceFrequency.DAILY) {
        return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Day", interval)}`
    } else if (frequency === RecurrenceFrequency.WEEKLY) {
        const weekdayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
        const weekdays = weeklyRecurrence.map((index) => weekdayNames[index])

        return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Week", interval)} on ${weekdays.join(", ")}`
    } else if (frequency === RecurrenceFrequency.MONTHLY) {
        const day = date.format("D")
        const dayOfWeek = date.format("dddd")

        const firstOfMonth = date.startOf("month")
        const occurrence = Math.floor((date.date() - firstOfMonth.date()) / 7) + 1
        // We're leveraging dayjs to get the ordinal suffix for the occurrence.
        // It doesn't have anything to do with dates, just a handy util.
        const occurrenceFormatted = dayjs().date(occurrence).format("Do")

        if (monthlyRecurrence === 0) {
            return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Month", interval)} on day ${day}`
        } else {
            return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Month", interval)} on the ${occurrenceFormatted} ${dayOfWeek}`
        }
    } else if (frequency === RecurrenceFrequency.YEARLY) {
        const month = date.format("MMMM")
        const day = date.format("D")
        return `Every ${interval > 1 ? `${interval} ` : ""}${pluralize("Year", interval)} on ${month} ${day}`
    }

    return "Unknown"
}
